import styled from '@emotion/styled';

import { Grid, Card, CardContent, CardHeader, Divider, useTheme, CardActionArea, Button } from '@mui/material';

import AddRounded from '@mui/icons-material/AddRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { BasicFlex, FullDivider } from '../../../utils/Common';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import useIsMobile from '../../../hooks/useIsMobile';

import RepairList from './RepairList';

// contexts
import { useFormikContext } from 'formik';
import { useDialogForm } from '../../../contexts/DialogForm';
import { useFormData } from '../../../contexts/FormData';

import Field from '../../../utils/FormikFields/Field';

import { formatCurrency, formatGenericDate } from '../../../utils/util';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import AddPayment from './AddPayment';

const SummaryCard = styled(Card)`
  width: 100%;
`

const SummaryTitle = styled(CardBlockHeader)`
  margin: 1.5rem 0;
`

const BalanceTotal = styled.div`
  min-width: fit-content;
  flex-grow: 1;
`

const BalanceAmount = styled.div`
  font-weight: ${props => props.$total ? '600' : '500'};
  font-size: ${props => props.$total ? '1.6rem' : '1.4rem'};
  color: ${props => props.$balance ? props.theme.colors.error : "inherit"};
`

const SwitchContainer = styled.div`
  margin-top: 1.5rem;
  padding-left: .5rem;
`

const BalanceLine = ({title, description, amount, total = false, balance = false}) => {
  let check_red = balance && +amount > 0 ? true : false
  const { isNotDesktop } = useIsMobile();
  return (
    <BasicFlex dir="row" justify="space-between" align="center">
      <BalanceTotal>{title}</BalanceTotal>
      <BasicFlex width="20rem">
        <div>{!isNotDesktop ? description : ""}</div>
        <BalanceAmount $total={total} $balance={check_red}>{formatCurrency(amount || 0)}</BalanceAmount>
      </BasicFlex>
    </BasicFlex>
  )
}


const getAppliedStatus = (past_balance, applied_payments_total) => {
  let status, amount = applied_payments_total;
  if (past_balance <= 0) {
    amount = 0;
  } else {
    if (applied_payments_total <= 0) {
      amount = 0;
    }
  }
  status = `Applied ${formatCurrency(amount)}`;
  return { status, amount };
}


const calculateAdjustments = (adjustments, total) => {
//  let selected = adjustments?.filter(el => el.selected === true) || [];
 let adjustmentTotal = 0;
 adjustments.forEach(el => {
  let adjustmentRate = el.rate_type === "percentage" ? (total * (el.rate / 100)) : el.rate;
   if (el.item_type === "charge") {
     adjustmentTotal += adjustmentRate;
   } else {
     adjustmentTotal -= adjustmentRate;
   }
 });
 return adjustmentTotal;
}

const BalanceDisplay = ({ balance }) => {
  let value = balance === 0 ? 'settled' : balance > 0 ? 'due' : 'credit';
  let formatted = Math.abs(balance);

  return (
  <BasicFlex justify="flex-end" gap=".6rem" width="auto"> 
  <BalanceAmount>{formatCurrency(formatted)}</BalanceAmount>
  <div style={{fontSize: "1.2rem"}}>
  <MultiStatusChip value={value} values={[{value: "settled", label: "Settled", status: "basic"}, {value: "due", label: "Due", status: "error"}, {value: "credit", label: "Credit", status: "success"}]} br="10px" py=".2rem" fs="1.2rem" /></div> 
  </BasicFlex>
)
}


const Sidebar = () => {
  const { values, submitForm } = useFormikContext() || {};
  const { formValues } = useFormData() || {};

  const { handleClose } = useDialogForm() || {};

  const theme = useTheme();

  let paymentStage = !!values?.invoice_id

  let customerInfo = formValues?.customer || {};

  let past_balance = customerInfo?.final_balance || 0;

  past_balance = past_balance - (values?.total || 0);

  let wigs_total = values?.wigs?.reduce((total, wig) => total + +wig.rate, 0);
  let services_total = values?.services?.reduce((total, service) => total + +service.rate, 0);
  let products_total = values?.products?.reduce((total, product) => total + (+product.rate * +product.quantity), 0);
  let repairs_total = values?.repairs?.reduce((total, repair) => total + +repair.rate, 0);
  let subtotal = services_total + products_total + wigs_total + repairs_total;
  let adjustments_total = calculateAdjustments(values?.adjustments, subtotal);
  let total = subtotal + adjustments_total;

  let new_payments_total = formValues?.new_payments?.reduce((acc, el) => acc + +el?.amount, 0) || 0;

  let available_credit = past_balance < 0 ? Math.abs(past_balance) : 0;

  let total_payments = values.apply_credit && paymentStage ? +available_credit + +new_payments_total : +new_payments_total;

  let applied_payments_total = formValues?.applied_payments?.filter(el => el?.selected)?.reduce((acc, el) => acc + +el?.amount, 0) || 0;

  let { status: applied_status, amount: applied_amount } = getAppliedStatus(past_balance, applied_payments_total);

  let grand_total = total + +past_balance;

  let invoice_balance = total - (values?.total_paid || 0);

  let total_items = values?.services?.length + values?.products?.length;


  const custDetails = [
    { header: "Customer", info: customerInfo.full_name, grow: true  },
    { header: "Past Balance", info: <BalanceDisplay balance={past_balance} />, grow: true  },
  ]
  
  return (
    <BasicFlex dir="column" gap="2rem">

      <SummaryCard variant='outlined'>
        <CardHeader title="Customer Information" />
        <CardContent >

          <CardBlockContainer>
            <CardBlockHeader>Customer Details</CardBlockHeader>
            {custDetails.map((block) => (
              <CardBlock key={block.header} {...block} />
            ))}
          </CardBlockContainer>
        </CardContent>
      </SummaryCard>

      {paymentStage ? <AddPayment invoice_balance={invoice_balance} /> : null }

      <SummaryCard variant='outlined'>
        <CardHeader title="Checkout Summary" />
        <CardContent >
          <BasicFlex dir="column" gap="1.5rem">
            {values?.wigs?.length ? <BalanceLine title="Wigs Total" description={values?.wigs?.length ? `${values?.wigs?.length} Wigs` : ""} amount={wigs_total} /> : null}

            {values?.services?.length ? <BalanceLine title="Services Total" description={values?.services?.length ? `${values?.services?.length} Services` : ""} amount={services_total} /> : null}

            {values?.products?.length ? <BalanceLine title="Products Total" description={values?.products?.length ? `${values?.products?.length} Products` : ""} amount={products_total} /> : null}

            {values?.repairs?.length ? <BalanceLine title="Repairs Total" description={values?.repairs?.length ? `${values?.repairs?.length} Repairs` : ""} amount={repairs_total} /> : null}

            <BalanceLine title="Sub Total" description={total_items ? `${total_items} Items` : ""} amount={subtotal} />
            <BalanceLine title="Discounts & Fees" description={values?.adjustments?.length ? `${values?.adjustments?.length} Adjustments` : ""} amount={adjustments_total} />
            
            <BalanceLine title="Total" amount={total} />
            {/* <BalanceLine title="Past Balance" description={applied_status} amount={past_balance} />
            <BalanceLine title="Grand Total" amount={grand_total} total={true} /> */}
            <BalanceLine title="Invoice Balance" amount={invoice_balance} />

            <Divider sx={{width: "100%"}} />
            {/* <BalanceLine title="Total Paid" amount={total_payments} /> */}
            <BalanceLine title="Opening Balance" description={applied_status} amount={past_balance} muted />
            <BalanceLine title="New Payments" amount={+new_payments_total} muted />
            {/* <BalanceLine title="New Balance" amount={grand_total - total_payments} total={true} balance={true} /> */}
            <BasicFlex>
              <div>Customer Balance</div>
              <BalanceDisplay balance={grand_total - +new_payments_total} />
            </BasicFlex>
          </BasicFlex>
        </CardContent>
         <Divider />
        <CardActionArea sx={{padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem"}}>
          {!paymentStage ? <Button fullWidth color="primary" variant="contained" onClick={(e) => {
            e.stopPropagation();
            submitForm()
          }} size="large" startIcon={<ArrowForwardRoundedIcon />}>Continue to Payment</Button> : null}
          
          {!paymentStage ? <Button fullWidth color="primary" variant="outlined" onClick={(e) => {
            e.stopPropagation();
            submitForm()
            if (handleClose) handleClose();
          }} size="large" startIcon={<ShoppingCartCheckoutRoundedIcon />}>Checkout without Payment</Button> : null}

          {paymentStage ?<Button fullWidth color="primary" variant="contained" onClick={(e) => {
            e.stopPropagation();
            submitForm()
            if (handleClose) handleClose();
          }} size="large" startIcon={<ShoppingCartCheckoutRoundedIcon />}>Checkout</Button> : null}
        </CardActionArea>
      </SummaryCard>

      <RepairList />

    </ BasicFlex>
  )
};

export default Sidebar;